import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, Ship, TrendingUp, Package, Truck, AlertTriangle, DollarSign, Building2, Shield } from 'lucide-react';
import { useLanguage, LanguageSwitcher } from '@/components/LanguageContext';

const Article6 = () => {
  const { t, language } = useLanguage();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="outline" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('common.backToHome')}
            </Button>
          </Link>
          
          <div className="flex items-center mb-4">
            <Badge className="bg-blue-600 text-white mr-4">
              <Ship className="h-3 w-3 mr-1" />
              Trade
            </Badge>
            <span className="text-sm text-gray-600">Kuehne+Nagel News</span>
          </div>
          
          <div className="flex items-center text-sm text-muted-foreground mb-6">
            <Calendar className="h-4 w-4 mr-1" />
            February 2026
            <span className="mx-2">•</span>
            <span>8 min read</span>
          </div>
          
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {t('article.6.title')}
          </h1>
          
          <img 
            src="https://external-preview.redd.it/eu-concerned-about-chinas-presence-in-major-ports-including-v0-pFWBLM74McSfbuNGTFX495haw07vNMJHt8qmP6NKlLc.jpeg?width=1080&crop=smart&auto=webp&s=076140ca1dbc7becc2e837946452613a2ab1b9c4" 
            alt="Rotterdam Port Container Terminal"
            className="w-full h-64 object-cover rounded-lg mb-8"
          />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 font-medium">
              {t('article.6.intro')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.6.section1.title')}</h2>
            
            <p className="mb-6">
              {t('article.6.p1')}
            </p>

            <p className="mb-6">
              {t('article.6.p2')}
            </p>

            <div className="bg-blue-50 p-6 rounded-lg my-8 border border-blue-100">
              <h3 className="font-bold text-blue-900 mb-2 flex items-center">
                <DollarSign className="mr-2" />
                {t('article.6.highlight.title')}
              </h3>
              <p className="text-sm text-blue-800">
                {t('article.6.highlight.content')}
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.6.section2.title')}</h2>
            
            <p className="mb-6">
              {t('article.6.p3')}
            </p>

            <p className="mb-6">
              {t('article.6.p4')}
            </p>

            <div className="bg-orange-50 p-6 rounded-lg my-8">
              <h3 className="font-bold text-orange-900 mb-4">{language === 'ko' ? '중국 항구 영향력 분석' : 'Chinese Port Influence Analysis'}</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-orange-800">
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <Building2 className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '로터담 항구 상황' : 'Rotterdam Port Situation'}
                  </h4>
                  <ul className="space-y-1">
                    <li><strong>{language === 'ko' ? '중국 기업:' : 'Chinese Companies:'}</strong> {language === 'ko' ? 'COSCO Shipping Ports' : 'COSCO Shipping Ports'}</li>
                    <li><strong>{language === 'ko' ? '지분율:' : 'Ownership:'}</strong> {language === 'ko' ? '주요 터미널 지분 보유' : 'Major terminal stakes'}</li>
                    <li><strong>{language === 'ko' ? '우려사항:' : 'Concerns:'}</strong> {language === 'ko' ? '전략적 인프라 통제' : 'Strategic infrastructure control'}</li>
                    <li><strong>{language === 'ko' ? '영향:' : 'Impact:'}</strong> {language === 'ko' ? '유럽 물류 네트워크' : 'European logistics network'}</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <Shield className="mr-1 h-4 w-4" />
                    {language === 'ko' ? 'EU 항구 전략' : 'EU Port Strategy'}
                  </h4>
                  <ul className="space-y-1">
                    <li><strong>{language === 'ko' ? '보고서:' : 'Report:'}</strong> {language === 'ko' ? 'EU 의회 연구 보고서' : 'European Parliament study'}</li>
                    <li><strong>{language === 'ko' ? '범위:' : 'Scope:'}</strong> {language === 'ko' ? '주요 유럽 항구들' : 'Major European ports'}</li>
                    <li><strong>{language === 'ko' ? '대응:' : 'Response:'}</strong> {language === 'ko' ? '외국인 투자 심사 강화' : 'Enhanced foreign investment screening'}</li>
                    <li><strong>{language === 'ko' ? '목표:' : 'Goal:'}</strong> {language === 'ko' ? '전략적 자율성 보장' : 'Ensure strategic autonomy'}</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.6.section3.title')}</h2>
            
            <p className="mb-6">
              {t('article.6.p5')}
            </p>

            <p className="mb-6">
              {t('article.6.p6')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.6.section4.title')}</h2>
            
            <p className="mb-6">
              {t('article.6.p7')}
            </p>

            <p className="mb-6">
              {t('article.6.p8')}
            </p>

            <div className="bg-red-50 p-6 rounded-lg mt-8">
              <h3 className="font-bold text-red-900 mb-4">{language === 'ko' ? '중국 항구 영향력 분석' : 'Chinese Port Influence Analysis'}</h3>
              <div className="space-y-4 text-sm text-red-800">
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <AlertTriangle className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '로테르담 항구 현황' : 'Rotterdam Port Situation'}
                  </h4>
                  <p>{language === 'ko' ? 
                    '로테르담에서 50% 이상의 중국 연결 컴테이너와 80%의 외국 기업 항구 공간 점유로 외국 통제 우려가 제기되고 있습니다.' : 
                    'Rotterdam sees over 50% China-linked containers and 80% foreign company occupation of port space, raising concerns about foreign control.'
                  }</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <Building2 className="mr-1 h-4 w-4" />
                    {language === 'ko' ? 'EU 항구 전략' : 'EU Port Strategy'}
                  </h4>
                  <p>{language === 'ko' ? 
                    '유럽 위원회가 외국 통제 우려를 다루는 새로운 항구 전략을 초안하여 중국 기업이 9%의 항구 지분을 차지하는 상황에서 전략적 자율성을 보장하려고 합니다.' : 
                    'European Commission drafts new Port Strategy addressing foreign control concerns, with Chinese companies holding 9% port share, aiming to ensure strategic autonomy.'
                  }</p>
                </div>
              </div>
            </div>
          </div>

          {/* Source Attribution */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              {t('common.sourceAttribution')} Kuehne+Nagel News - India-EU trade deal to favour some shipping sectors.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t('common.sourceDetails')}
            </p>
            <a 
              href="https://nltimes.nl/2026/02/24/eu-concerned-chinas-presence-major-ports-including-rotterdam-report" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                {t('common.readOriginalArticle')}
              </Button>
            </a>
          </div>

          {/* Navigation */}
          <div className="mt-8 flex justify-between">
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('common.backToAllArticles')}
              </Button>
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Article6;